import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Typography, Select, TimePicker, Row, Col } from 'antd';
import { BankOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MainLayout from '../layout/layout';
import { apiClient } from '../../config/apiClient';

const { TextArea } = Input;
const { Text } = Typography;

const DatosClub = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoBase64, setFotoBase64] = useState(null);
  const [fotoTipo, setFotoTipo] = useState(null);
  const [fotoRemoved, setFotoRemoved] = useState(false);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingLocalidades, setLoadingLocalidades] = useState(false);

  const loadPaises = async () => {
    try {
      const response = await apiClient.get('/paises');
      if (response.ok) {
        const data = await response.json();
        setPaises(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error al cargar países:', e);
    }
  };

  const loadProvincias = async (paisId) => {
    if (!paisId) {
      setProvincias([]);
      return;
    }
    setLoadingProvincias(true);
    try {
      const response = await apiClient.get(`/provincias/pais/${paisId}`);
      if (response.ok) {
        const data = await response.json();
        setProvincias(Array.isArray(data) ? data : []);
      } else {
        setProvincias([]);
      }
    } catch (e) {
      console.error('Error al cargar provincias:', e);
      setProvincias([]);
    } finally {
      setLoadingProvincias(false);
    }
  };

  const loadLocalidades = async (provinciaId) => {
    if (!provinciaId) {
      setLocalidades([]);
      return;
    }
    setLoadingLocalidades(true);
    try {
      const response = await apiClient.get(`/localidades/provincia/${provinciaId}`);
      if (response.ok) {
        const data = await response.json();
        setLocalidades(Array.isArray(data) ? data : []);
      } else {
        setLocalidades([]);
      }
    } catch (e) {
      console.error('Error al cargar localidades:', e);
      setLocalidades([]);
    } finally {
      setLoadingLocalidades(false);
    }
  };

  const loadEmpresa = async () => {
    setLoading(true);
    try {
      await loadPaises();
      const response = await apiClient.get('/empresa');
      if (response.ok) {
        const data = await response.json();
        const paisId = data.emp_pais_id != null ? Number(data.emp_pais_id) : undefined;
        const provinciaId = data.emp_provincia_id != null ? Number(data.emp_provincia_id) : undefined;
        const localidadId = data.emp_localidad != null ? Number(data.emp_localidad) : undefined;

        if (paisId) await loadProvincias(paisId);
        if (provinciaId) await loadLocalidades(provinciaId);

        form.setFieldsValue({
          emp_nombre: data.emp_nombre ?? '',
          emp_direccion: data.emp_direccion ?? '',
          emp_latitud: data.emp_latitud != null ? String(data.emp_latitud) : '',
          emp_longitud: data.emp_longitud != null ? String(data.emp_longitud) : '',
          emp_telefono: data.emp_telefono ?? '',
          emp_instagram: data.emp_instagram ?? '',
          emp_horario_desde: data.emp_horario_desde ? dayjs(data.emp_horario_desde, 'HH:mm') : null,
          emp_horario_hasta: data.emp_horario_hasta ? dayjs(data.emp_horario_hasta, 'HH:mm') : null,
          emp_pais_id: paisId,
          emp_provincia_id: provinciaId,
          emp_localidad: localidadId,
          emp_descripcion: data.emp_descripcion ?? '',
        });
        if (data.emp_foto_base64) {
          setFotoBase64(data.emp_foto_base64);
          setFotoTipo(data.emp_foto_tipo || 'image/jpeg');
          setFotoPreview(`data:${data.emp_foto_tipo || 'image/jpeg'};base64,${data.emp_foto_base64}`);
        } else {
          setFotoBase64(null);
          setFotoTipo(null);
          setFotoPreview(null);
          setFotoRemoved(false);
        }
      }
    } catch (error) {
      console.error('Error al cargar datos del club:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmpresa();
  }, []);

  const handlePaisChange = (paisId) => {
    form.setFieldsValue({ emp_provincia_id: undefined, emp_localidad: undefined });
    setProvincias([]);
    setLocalidades([]);
    loadProvincias(paisId);
  };

  const handleProvinciaChange = (provinciaId) => {
    form.setFieldsValue({ emp_localidad: undefined });
    setLocalidades([]);
    loadLocalidades(provinciaId);
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.warning('Elegí una imagen (JPG, PNG, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.startsWith('data:')) {
        const base64 = result.split(',')[1];
        setFotoBase64(base64);
        setFotoTipo(file.type);
        setFotoPreview(result);
        setFotoRemoved(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const quitarFoto = () => {
    setFotoBase64(null);
    setFotoTipo(null);
    setFotoPreview(null);
    setFotoRemoved(true);
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const body = {
        emp_nombre: values.emp_nombre,
        emp_direccion: values.emp_direccion,
        emp_latitud: values.emp_latitud?.trim() ? values.emp_latitud.trim() : null,
        emp_longitud: values.emp_longitud?.trim() ? values.emp_longitud.trim() : null,
        emp_telefono: values.emp_telefono,
        emp_instagram: values.emp_instagram,
        emp_horario_desde: values.emp_horario_desde ? values.emp_horario_desde.format('HH:mm') : null,
        emp_horario_hasta: values.emp_horario_hasta ? values.emp_horario_hasta.format('HH:mm') : null,
        emp_localidad: values.emp_localidad ?? null,
        emp_descripcion: values.emp_descripcion,
        emp_foto_base64: fotoBase64 || undefined,
        emp_foto_tipo: fotoTipo || undefined,
        clearFoto: fotoRemoved,
      };
      const response = await apiClient.put('/empresa', body);
      if (response.ok) {
        message.success('Datos del club guardados correctamente');
        loadEmpresa();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      message.error('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <Card
        title={
          <span>
            <BankOutlined style={{ marginRight: 8 }} />
            Datos del club
          </span>
        }
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            {/* Fila 1: Nombre, Dirección, Teléfono (3 columnas) */}
            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_nombre" label="Nombre" rules={[{ required: false }]}>
                  <Input placeholder="Nombre del club o empresa" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_direccion" label="Dirección" rules={[{ required: false }]}>
                  <Input placeholder="Dirección" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_telefono" label="Teléfono" rules={[{ required: false }]}>
                  <Input placeholder="Teléfono" />
                </Form.Item>
              </Col>
            </Row>

            {/* Fila 1b: Latitud y Longitud (opcionales, después de Dirección) */}
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item name="emp_latitud" label="Latitud (opcional)" rules={[{ required: false }]}>
                  <Input placeholder="Ej: -32.4085" type="number" step="any" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="emp_longitud" label="Longitud (opcional)" rules={[{ required: false }]}>
                  <Input placeholder="Ej: -63.2596" type="number" step="any" />
                </Form.Item>
              </Col>
            </Row>

            {/* Fila 2: País, Provincia, Localidad (3 columnas) */}
            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_pais_id" label="País" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar país"
                    allowClear
                    onChange={handlePaisChange}
                    options={paises.map((p) => ({ value: p.pai_id, label: p.pai_nombre }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_provincia_id" label="Provincia" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar provincia"
                    allowClear
                    loading={loadingProvincias}
                    onChange={handleProvinciaChange}
                    options={provincias.map((p) => ({ value: p.pro_id, label: p.pro_nombre }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_localidad" label="Localidad" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar localidad"
                    allowClear
                    loading={loadingLocalidades}
                    options={localidades.map((l) => ({ value: l.loc_id, label: l.loc_nombre }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Fila 3: Instagram + Horarios (3 columnas) */}
            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_instagram" label="Instagram" rules={[{ required: false }]}>
                  <Input placeholder="Usuario o enlace de Instagram" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_horario_desde" label="Horario (desde)" rules={[{ required: false }]}>
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="emp_horario_hasta" label="Horario (hasta)" rules={[{ required: false }]}>
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="emp_descripcion" label="Descripción" rules={[{ required: false }]}>
              <TextArea rows={4} placeholder="Descripción del club" />
            </Form.Item>

            <Form.Item label="Foto">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {fotoPreview ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <img
                      src={fotoPreview}
                      alt="Vista previa"
                      style={{
                        maxWidth: 200,
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #404040',
                      }}
                    />
                    <div>
                      <Button type="default" danger icon={<DeleteOutlined />} onClick={quitarFoto} size="small">
                        Quitar foto
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="foto-club-input"
                  />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => document.getElementById('foto-club-input').click()}
                  >
                    {fotoPreview ? 'Cambiar foto' : 'Subir foto'}
                  </Button>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    Se guarda en la base de datos (BLOB)
                  </Text>
                </div>
              </div>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Guardar
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
};

export default DatosClub;
